// Problem Record store — prototype persistence layer.
//
// Seed records live in code; records created from Top 5 Intelligence are held
// in localStorage. Everything is returned as the same `ProblemRecord` shape so
// this module can be swapped for a database repository later without touching
// the UI.

import { useSyncExternalStore } from "react";
import {
  emptyRootCause,
  problemRecords,
  STAGE_TEMPLATE,
  type EvidenceClaim,
  type ManagementStatus,
  type ProblemRecord,
  type ProblemStage,
} from "./problem-record-data";
import { STAGE_REQUIREMENTS } from "./problem-record-workflow";
import {
  claimsByItem,
  claimsForComponent,
  claimsForItem,
  currency,
  type Top5Item,
  type Top5View,
} from "./top5-data";

const STORAGE_KEY = "broadview.problem-records.v1";

/** Prototype "today" — keeps the demo deterministic. */
export const PROTOTYPE_TODAY = "2026-08-28";

export const top5SourceLabel: Record<Top5View, string> = {
  problems: "Top 5 Problems",
  components: "Top 5 Components",
  products: "Top 5 Products",
  carlines: "Top 5 Car Lines",
  suppliers: "Top 5 Suppliers",
  "failure-modes": "Top 5 Failure Modes",
};

// ---------------------------------------------------------------------------
// Lightweight seed records (summarised — no fabricated downstream detail)
// ---------------------------------------------------------------------------

function stages(activeKey: string, completeKeys: string[], owner: string): ProblemStage[] {
  return STAGE_TEMPLATE.map((s) => ({
    ...s,
    state: completeKeys.includes(s.key) ? "Complete" : s.key === activeKey ? "Active" : "Pending",
    owner,
    summary: "",
  }));
}

const summaryRecords: ProblemRecord[] = [
  {
    id: "PR-2026-016",
    title: "Traction inverter IGBT thermal derate — InverterDrive ID-8",
    status: "Active — Root Cause",
    severity: "High",
    safetySeverity: "S2-Loss of propulsion (no fire risk identified)",
    riskScore: 74,
    owner: "A. Ricci — Powertrain Quality",
    sponsor: "D. Shumar — VP Quality",
    openedOn: "2026-06-02",
    daysOpen: 87,
    source: "Top 5 Components · Traction Inverter",
    products: ["InverterDrive ID-8"],
    carLines: ["EV-X Platform", "EV-X Long Range"],
    component: "Traction Inverter",
    supplier: "Helios Power Systems",
    failureMode: "Thermal derate / power stage overheat",
    totalClaims: 291,
    warrantyCost: 1560000,
    claimsPer1000: 23.4,
    linkedCaseId: "8D-2044",
    top5ItemId: "C-02",
    narrative:
      "Sustained-load derate events on SiC power stage. Containment (thermal interface rework bulletin) is in force while root-cause analysis on gate-driver junction temperature margin continues with the supplier.",
    spcSignal: "Claims SPC (Traction Inverter) — Jan '26 point above UCL",
    predictiveSignal: "Predictive alert: 61% risk within 60 days · 3,180 units exposed",
    origin: "seed",
    trend: "Rising",
    trendPct: 12.7,
    repeatOccurrence: false,
    repeatCount: 0,
    customerImpact: 76,
    attention: "Root cause stage open 41 days — target 30",
    stages: stages("root-cause", ["signal", "definition", "containment"], "A. Ricci"),
    eightD: [
      {
        code: "D1",
        label: "Team",
        state: "Complete",
        owner: "A. Ricci",
        note: "Powertrain QE, supplier engineering and thermal CAE chartered.",
      },
      {
        code: "D2",
        label: "Problem Description",
        state: "Complete",
        owner: "A. Ricci",
        note: "291 claims, $1.56M exposure, 23.4 C/1000 across EV-X.",
      },
      {
        code: "D3",
        label: "Interim Containment",
        state: "Complete",
        owner: "Service Ops",
        note: "Thermal interface rework bulletin issued to dealer network.",
      },
      {
        code: "D4",
        label: "Root Cause",
        state: "Active",
        owner: "A. Ricci",
        note: "Gate-driver junction temperature margin under investigation.",
      },
      {
        code: "D5",
        label: "Permanent Corrective Action",
        state: "Pending",
        owner: "A. Ricci",
        note: "",
      },
      { code: "D6", label: "Implement & Validate", state: "Pending", owner: "—", note: "" },
      { code: "D7", label: "Prevent Recurrence", state: "Pending", owner: "—", note: "" },
      {
        code: "D8",
        label: "Team Recognition / Closure",
        state: "Pending",
        owner: "D. Shumar",
        note: "",
      },
    ],
    rootCause: { ...emptyRootCause, method: "Ishikawa + thermal CAE correlation (in progress)" },
    correctiveActions: [
      {
        id: "CA-001",
        type: "Containment",
        action: "Thermal interface rework per service bulletin SB-26-114",
        owner: "Service Ops",
        due: "2026-06-20",
        status: "Complete",
        evidence: "SB-26-114, 1,940 units reworked",
      },
    ],
    engineeringChanges: [],
    validation: [],
    effectiveness: [],
    activity: [
      {
        ts: "2026-08-12 10:04",
        actor: "A. Ricci",
        action: "Supplier thermal CAE correlation review held",
        clause: "IATF 16949 §8.4.2.4",
      },
      {
        ts: "2026-06-02 08:15",
        actor: "System",
        action: "Problem Record opened from Top 5 Components",
        clause: "IATF 16949 §9.1.1.1",
      },
    ],
  },
  {
    id: "PR-2026-011",
    title: "ABS caliper seal residual brake drag — Hybrid Series",
    status: "Active — Effectiveness",
    severity: "High",
    safetySeverity: "S2-Reduced braking efficiency / thermal event risk",
    riskScore: 63,
    owner: "J. Novak — Chassis Quality",
    sponsor: "D. Shumar — VP Quality",
    openedOn: "2026-03-09",
    daysOpen: 172,
    source: "Top 5 Problems · ABS caliper seal brake drag",
    products: ["Hybrid Series"],
    carLines: ["Hybrid Series"],
    component: "Brake System",
    supplier: "FrenoTech S.p.A.",
    failureMode: "Elastomer swell / residual drag",
    totalClaims: 231,
    warrantyCost: 980000,
    claimsPer1000: 17.8,
    linkedCaseId: "8D-2038",
    top5ItemId: "P-03",
    narrative:
      "Seal elastomer incompatible with the updated brake fluid specification caused swell and residual clamp load. Corrective seal material change is released; effectiveness monitoring runs until three consecutive months below 6 C/1000.",
    spcSignal: "Claims SPC (Brake System) — in control since 2026-06",
    predictiveSignal: "Predictive alert cleared — residual population 2,140 units",
    origin: "seed",
    trend: "Stable",
    trendPct: 1.4,
    repeatOccurrence: true,
    repeatCount: 1,
    customerImpact: 71,
    stages: stages(
      "effectiveness",
      [
        "signal",
        "definition",
        "containment",
        "root-cause",
        "corrective",
        "eco",
        "pfmea",
        "validation",
      ],
      "J. Novak",
    ),
    eightD: [
      {
        code: "D1",
        label: "Team",
        state: "Complete",
        owner: "J. Novak",
        note: "Chassis QE, materials lab and supplier team chartered.",
      },
      {
        code: "D2",
        label: "Problem Description",
        state: "Complete",
        owner: "J. Novak",
        note: "231 claims, $980K exposure, 17.8 C/1000 on Hybrid Series.",
      },
      {
        code: "D3",
        label: "Interim Containment",
        state: "Complete",
        owner: "SQE",
        note: "Seal lot LT-77 quarantined; dealer inspection campaign issued.",
      },
      {
        code: "D4",
        label: "Root Cause",
        state: "Complete",
        owner: "J. Novak",
        note: "EPDM seal incompatible with revised DOT-4 LV fluid — swell 4.8%.",
      },
      {
        code: "D5",
        label: "Permanent Corrective Action",
        state: "Complete",
        owner: "J. Novak",
        note: "Seal material changed to HNBR; fluid compatibility test added.",
      },
      {
        code: "D6",
        label: "Implement & Validate",
        state: "Complete",
        owner: "Test",
        note: "Drag torque and 500h fluid soak validation passed.",
      },
      {
        code: "D7",
        label: "Prevent Recurrence",
        state: "Active",
        owner: "J. Novak",
        note: "Material compatibility gate being added to the design standard.",
      },
      {
        code: "D8",
        label: "Team Recognition / Closure",
        state: "Pending",
        owner: "D. Shumar",
        note: "",
      },
    ],
    rootCause: {
      statement:
        "EPDM caliper seal material is chemically incompatible with the revised DOT-4 LV brake fluid, swelling 4.8% and producing residual clamp load after brake release.",
      occurrence:
        "Fluid specification revised without re-running elastomer compatibility qualification.",
      detection: "Drag torque was not measured at end-of-line or in service diagnostics.",
      systemic:
        "No change-control gate linking fluid specification changes to seal material requalification.",
      method: "5-Why + materials lab immersion testing",
      fiveWhys: [
        "Why does the brake drag? — Residual clamp load after release.",
        "Why residual clamp load? — Seal roll-back geometry lost to swell.",
        "Why did the seal swell? — Elastomer incompatible with the revised fluid.",
        "Why was an incompatible elastomer used? — Fluid spec changed without requalification.",
        "Why no requalification? — No change-control link between fluid and seal specs.",
      ],
      verification:
        "Turn-on / turn-off verified: EPDM seals reproduced drag in 9 of 10 soaks; HNBR seals in 0 of 10.",
    },
    correctiveActions: [
      {
        id: "CA-001",
        type: "Containment",
        action: "Quarantine seal lot LT-77 and issue dealer inspection campaign",
        owner: "SQE",
        due: "2026-03-20",
        status: "Complete",
        evidence: "Campaign 26-B-07, 3,410 vehicles inspected",
      },
      {
        id: "CA-002",
        type: "Corrective",
        action: "Change caliper seal material from EPDM to HNBR",
        owner: "J. Novak",
        due: "2026-05-30",
        status: "Complete",
        evidence: "ECO-4390 released",
      },
      {
        id: "CA-003",
        type: "Preventive",
        action: "Add fluid/elastomer compatibility gate to chassis design standard",
        owner: "J. Novak",
        due: "2026-09-30",
        status: "In Progress",
        evidence: "DS-CHS-021 draft",
      },
    ],
    engineeringChanges: [
      {
        id: "ECO-4390",
        title: "Caliper seal material EPDM → HNBR",
        changeLevel: "Design",
        status: "Released",
        releasedOn: "2026-05-30",
        affectedParts: "CAL-HYB-1/2",
        ppapLevel: "Level 3",
        owner: "J. Novak",
        detail: "Material change with fluid compatibility evidence as a PPAP deliverable.",
      },
    ],
    validation: [
      {
        id: "VAL-01",
        test: "500 h fluid soak — seal swell",
        method: "ASTM D471",
        sample: "n = 10 seals",
        acceptance: "Swell ≤ 1.5%",
        result: "Mean 0.6%",
        status: "Pass",
        completedOn: "2026-06-18",
      },
      {
        id: "VAL-02",
        test: "Residual drag torque",
        method: "TP-BRK-042",
        sample: "n = 12 calipers",
        acceptance: "≤ 2.0 Nm",
        result: "Mean 0.9 Nm",
        status: "Pass",
        completedOn: "2026-06-25",
      },
    ],
    effectiveness: [
      {
        metric: "Claims / 1,000 (post-change build)",
        baseline: "17.8",
        current: "5.1",
        target: "≤ 6.0",
        status: "On Target",
        window: "3 months post-ECO",
      },
      {
        metric: "SPC state",
        baseline: "Trend above CL",
        current: "In control",
        target: "In control 3 consecutive months",
        status: "Improving",
        window: "2 of 3 months achieved",
      },
    ],
    activity: [
      {
        ts: "2026-08-20 09:30",
        actor: "J. Novak",
        action: "Effectiveness review month 2 — on target",
        clause: "IATF 16949 §10.2.3",
      },
      {
        ts: "2026-05-30 15:10",
        actor: "J. Novak",
        action: "ECO-4390 released",
        clause: "IATF 16949 §8.3.6.1",
      },
      {
        ts: "2026-03-09 07:45",
        actor: "System",
        action: "Problem Record opened from Top 5 Problems",
        clause: "IATF 16949 §9.1.1.1",
      },
    ],
  },
  {
    id: "PR-2026-008",
    title: "Infotainment cold-start boot loop — ControlNode K2",
    status: "Closed — Prevention verified",
    severity: "Medium",
    safetySeverity: "S3-Non-safety (loss of rear camera display)",
    riskScore: 28,
    owner: "S. Idris — Electronics Quality",
    sponsor: "D. Shumar — VP Quality",
    openedOn: "2026-01-14",
    daysOpen: 158,
    source: "Top 5 Failure Modes · Firmware watchdog race",
    products: ["ControlNode K2"],
    carLines: ["ICE Legacy"],
    component: "Infotainment Head Unit",
    supplier: "Lumen Electronics",
    failureMode: "Firmware watchdog race",
    totalClaims: 197,
    warrantyCost: 410000,
    claimsPer1000: 14.2,
    linkedCaseId: "8D-2036",
    top5ItemId: "FM-04",
    narrative:
      "Init-sequence race in bootloader 4.x triggered a watchdog reset loop on cold soak below -15 °C. OTA 4.2.1 corrected the sequence, cold-soak regression was added to the release gate, and closure was signed off after three clean months.",
    spcSignal: "Claims SPC (Infotainment) — in control, trending down",
    predictiveSignal: "No active predictive alert",
    origin: "seed",
    trend: "Falling",
    trendPct: -22.6,
    repeatOccurrence: true,
    repeatCount: 3,
    customerImpact: 44,
    stages: STAGE_TEMPLATE.map((s) => ({
      ...s,
      state: "Complete" as const,
      owner: "S. Idris",
      summary: "",
    })),
    eightD: [
      {
        code: "D1",
        label: "Team",
        state: "Complete",
        owner: "S. Idris",
        note: "Electronics QE, supplier firmware team and validation chartered.",
      },
      {
        code: "D2",
        label: "Problem Description",
        state: "Complete",
        owner: "S. Idris",
        note: "197 claims, $410K exposure, cold-soak dependent.",
      },
      {
        code: "D3",
        label: "Interim Containment",
        state: "Complete",
        owner: "Service Ops",
        note: "Dealer reflash procedure issued for affected VINs.",
      },
      {
        code: "D4",
        label: "Root Cause",
        state: "Complete",
        owner: "S. Idris",
        note: "Race between display init and watchdog kick below -15 °C.",
      },
      {
        code: "D5",
        label: "Permanent Corrective Action",
        state: "Complete",
        owner: "S. Idris",
        note: "Bootloader 4.2.1 sequencing fix, OTA deployed.",
      },
      {
        code: "D6",
        label: "Implement & Validate",
        state: "Complete",
        owner: "Test",
        note: "Cold-soak regression at -30 °C, 200 cycles, zero failures.",
      },
      {
        code: "D7",
        label: "Prevent Recurrence",
        state: "Complete",
        owner: "S. Idris",
        note: "Cold-soak boot regression added to firmware release gate.",
      },
      {
        code: "D8",
        label: "Team Recognition / Closure",
        state: "Complete",
        owner: "D. Shumar",
        note: "Closed 2026-06-21 after three clean months.",
      },
    ],
    rootCause: {
      statement:
        "Bootloader 4.x initialises the display controller in parallel with the watchdog service; below -15 °C the display init exceeds the watchdog window and forces a reset loop.",
      occurrence:
        "Parallel init sequencing introduced in bootloader 4.0 without a cold-temperature timing budget.",
      detection: "Release validation ran at ambient only; no cold-soak boot regression existed.",
      systemic: "Firmware release gate lacked an environmental boot regression requirement.",
      method: "5-Why + trace-level boot instrumentation",
      fiveWhys: [
        "Why does the unit reboot? — Watchdog expires during init.",
        "Why does the watchdog expire? — Display init exceeds the kick window.",
        "Why does init take longer? — Panel driver timing degrades below -15 °C.",
        "Why wasn't the margin sized for cold? — No cold timing budget in bootloader 4.0.",
        "Why no cold validation? — Release gate had no environmental boot regression.",
      ],
      verification:
        "Turn-on / turn-off verified: 4.0 firmware looped in 18 of 20 cold soaks; 4.2.1 in 0 of 20.",
    },
    correctiveActions: [
      {
        id: "CA-001",
        type: "Containment",
        action: "Dealer reflash procedure for affected VIN population",
        owner: "Service Ops",
        due: "2026-02-05",
        status: "Complete",
        evidence: "Bulletin SB-26-031",
      },
      {
        id: "CA-002",
        type: "Corrective",
        action: "Bootloader 4.2.1 init sequencing fix released OTA",
        owner: "S. Idris",
        due: "2026-04-02",
        status: "Complete",
        evidence: "OTA 4.2.1, 96.7% uptake",
      },
      {
        id: "CA-003",
        type: "Preventive",
        action: "Add -30 °C cold-soak boot regression to firmware release gate",
        owner: "S. Idris",
        due: "2026-05-15",
        status: "Complete",
        evidence: "Release gate RG-FW-07 rev 3",
      },
    ],
    engineeringChanges: [
      {
        id: "ECO-4302",
        title: "Bootloader 4.2.1 init sequencing",
        changeLevel: "Spec",
        status: "Released",
        releasedOn: "2026-04-02",
        affectedParts: "HU-K2-ALL",
        ppapLevel: "Software change notice",
        owner: "S. Idris",
        detail: "Serialises display init ahead of watchdog arming with a cold timing budget.",
      },
    ],
    validation: [
      {
        id: "VAL-01",
        test: "Cold-soak boot regression",
        method: "TP-ELE-066, -30 °C",
        sample: "200 boot cycles",
        acceptance: "Zero reset loops",
        result: "0 / 200",
        status: "Pass",
        completedOn: "2026-04-18",
      },
    ],
    effectiveness: [
      {
        metric: "Claims / 1,000",
        baseline: "14.2",
        current: "1.3",
        target: "≤ 3.0",
        status: "On Target",
        window: "3 months post-OTA",
      },
      {
        metric: "Repeat occurrences",
        baseline: "3 prior",
        current: "0",
        target: "0",
        status: "On Target",
        window: "Since closure",
      },
    ],
    activity: [
      {
        ts: "2026-06-21 16:00",
        actor: "D. Shumar",
        action: "Problem Record closed — prevention verified",
        clause: "ISO 9001:2015 §10.2.2",
      },
      {
        ts: "2026-04-02 11:20",
        actor: "S. Idris",
        action: "OTA 4.2.1 released to field",
        clause: "IATF 16949 §8.5.6.1",
      },
      {
        ts: "2026-01-14 08:02",
        actor: "System",
        action: "Problem Record opened from Top 5 Failure Modes",
        clause: "IATF 16949 §9.1.1.1",
      },
    ],
  },
];

const rawSeedRecords: ProblemRecord[] = [
  ...problemRecords.map((r) => ({ ...r, origin: "seed" as const })),
  ...summaryRecords,
];

// ---------------------------------------------------------------------------
// Normalisation — maps legacy seed shapes onto the workflow model
// ---------------------------------------------------------------------------

const allClaims: Record<string, EvidenceClaim> = {};
for (const bucket of Object.values(claimsByItem)) {
  for (const c of bucket) allClaims[c.id] = c;
}

export function claimsById(ids: string[]): EvidenceClaim[] {
  return ids.map((id) => allClaims[id]).filter(Boolean);
}

function deriveManagementStatus(r: ProblemRecord): ManagementStatus {
  if (r.managementStatus) return r.managementStatus;
  const s = r.status.toLowerCase();
  if (s.startsWith("closed")) return "Closed";
  if (s.startsWith("draft") || r.isDraft) return "Draft";
  if (s.startsWith("on hold")) return "On Hold";
  if (s.startsWith("escalated")) return "Escalated";
  return "Active";
}

/** Pull the rich seed content into each stage's evidence fields (never fabricates). */
function hydrateStage(r: ProblemRecord, s: ProblemStage): ProblemStage {
  const req = STAGE_REQUIREMENTS[s.key];
  const evidence: Record<string, string> = { ...(s.evidence ?? {}) };
  const put = (k: string, v?: string | number | null) => {
    if (evidence[k] === undefined && v !== undefined && v !== null && String(v).trim() !== "") {
      evidence[k] = String(v);
    }
  };

  switch (s.key) {
    case "signal":
      put("source", r.source);
      put("evidenceConfirmed", (r.linkedClaimIds?.length ?? 0) > 0 ? "Yes" : "");
      put(
        "exposure",
        `${r.totalClaims.toLocaleString()} claims · ${currency(r.warrantyCost)} · ${r.claimsPer1000.toFixed(1)} C/1000`,
      );
      break;
    case "definition":
      put("problemStatement", r.narrative);
      put("ownerAssigned", r.owner);
      put("scope", [...r.products, ...r.carLines].join(", "));
      put("severityConfirmed", s.state === "Complete" ? "Yes" : "");
      break;
    case "containment": {
      const c = r.correctiveActions.filter((a) => a.type === "Containment");
      if (c.length) {
        put("action", c.map((a) => a.action).join(" · "));
        put("owner", c[0].owner);
        put("due", c[0].due);
        put("population", r.carLines.join(", "));
        put(
          "evidence",
          c
            .map((a) => a.evidence)
            .filter(Boolean)
            .join(" · "),
        );
      }
      break;
    }
    case "root-cause":
      put("occurrence", r.rootCause.occurrence);
      put("detection", r.rootCause.detection);
      put("systemic", r.rootCause.systemic);
      put("method", r.rootCause.method);
      put("verification", r.rootCause.verification);
      break;
    case "corrective": {
      const c = r.correctiveActions.filter((a) => a.type !== "Containment");
      if (c.length) {
        put("action", c.map((a) => a.action).join(" · "));
        put("owner", c[0].owner);
        put("due", c[0].due);
        put("rootCauseLink", r.rootCause.statement);
      }
      break;
    }
    case "eco":
      if (r.engineeringChanges.length) {
        const e = r.engineeringChanges[0];
        put("changeRequired", "Yes");
        put("changeId", r.engineeringChanges.map((x) => x.id).join(", "));
        put("affected", e.affectedParts);
        put("owner", e.owner);
        put("releaseStatus", `${e.status}${e.releasedOn ? ` · ${e.releasedOn}` : ""}`);
      }
      break;
    case "pfmea":
      if (s.state === "Complete" && s.summary) {
        put("pfmeaRequired", "Yes");
        put("controlPlanRequired", "Yes");
        put("linkedIds", r.linkedCaseId);
        put("risk", s.summary);
      }
      break;
    case "validation":
      if (r.validation.length) {
        const v = r.validation[0];
        put("plan", r.validation.map((x) => `${x.id} ${x.test}`).join(" · "));
        put("acceptance", v.acceptance);
        put("sample", `${v.sample} · ${v.method}`);
        put("result", r.validation.map((x) => `${x.id}: ${x.result} (${x.status})`).join(" · "));
        put("verdict", r.validation.every((x) => x.status === "Pass") ? "Pass" : "");
      }
      break;
    case "effectiveness":
      if (r.effectiveness.length) {
        const e = r.effectiveness[0];
        put("baseline", e.baseline);
        put("post", e.current);
        put("target", e.target);
        put("window", e.window);
        put("conclusion", s.state === "Complete" ? s.summary : "");
      }
      break;
    case "read-across":
      put("lesson", s.summary);
      put("applicability", s.state === "Complete" ? s.summary : "");
      break;
    case "prevention":
      if (s.state === "Complete") {
        put("prevention", s.summary);
        put("closure", r.narrative);
      }
      break;
  }

  const approvalRequired = req?.approvalRequired ?? false;
  return {
    ...s,
    evidence,
    approvalRequired,
    approvalStatus:
      s.approvalStatus ??
      (!approvalRequired ? "Not Required" : s.state === "Complete" ? "Approved" : "Pending"),
    approver: s.approver ?? (approvalRequired && s.state === "Complete" ? r.sponsor : undefined),
  };
}

export function normalizeRecord(r: ProblemRecord): ProblemRecord {
  const linkedClaimIds =
    r.linkedClaimIds ??
    (r.origin === "seed" ? claimsForComponent(r.component).map((c) => c.id) : []);
  const base: ProblemRecord = {
    ...r,
    managementStatus: deriveManagementStatus(r),
    linkedClaimIds,
    linkedClaims: r.linkedClaims ?? claimsById(linkedClaimIds),
    sourceKey: r.sourceKey ?? (r.top5ItemId ? `top5:${r.top5ItemId}` : undefined),
  };
  return { ...base, stages: base.stages.map((s) => hydrateStage(base, s)) };
}

export const seedProblemRecords: ProblemRecord[] = rawSeedRecords.map(normalizeRecord);

// ---------------------------------------------------------------------------
// Local persistence — created records plus workflow overrides on seed records
// ---------------------------------------------------------------------------

let cache: ProblemRecord[] | null = null;
const listeners = new Set<() => void>();

function readStorage(): ProblemRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ProblemRecord[]) : [];
  } catch {
    return [];
  }
}

function emit() {
  cache = null;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ProblemRecord[] {
  if (!cache) {
    const byId = new Map<string, ProblemRecord>(seedProblemRecords.map((r) => [r.id, r]));
    for (const stored of readStorage()) byId.set(stored.id, normalizeRecord(stored));
    cache = Array.from(byId.values());
  }
  return cache;
}

function getServerSnapshot(): ProblemRecord[] {
  return seedProblemRecords;
}

export function useProblemRecords(): ProblemRecord[] {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function getAllProblemRecords(): ProblemRecord[] {
  return getSnapshot();
}

export function findProblemRecord(id: string): ProblemRecord | undefined {
  return getSnapshot().find((r) => r.id.toLowerCase() === id.toLowerCase());
}

/** Stable source key for a Top 5 item — used to prevent duplicate escalations. */
export function top5SourceKey(item: Top5Item): string {
  return `top5:${item.id}`;
}

/** Dynamic linkage: the Problem Record (if any) that owns this Top 5 issue. */
export function recordForTop5Item(
  records: ProblemRecord[],
  item: Top5Item,
): ProblemRecord | undefined {
  const key = top5SourceKey(item);
  return records.find((r) => r.sourceKey === key || r.top5ItemId === item.id);
}

export function saveProblemRecord(record: ProblemRecord) {
  if (typeof window === "undefined") return;
  const created = readStorage().filter((r) => r.id !== record.id);
  created.push(record);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(created));
  emit();
}

export function clearCreatedProblemRecords() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
  emit();
}

/** Deterministic prototype sequence: seeds end at 016, so creation starts at 017. */
export function nextProblemRecordId(): string {
  const nums = getSnapshot()
    .map((r) => Number.parseInt(r.id.replace(/^PR-\d{4}-/, ""), 10))
    .filter((n) => Number.isFinite(n));
  const next = (nums.length ? Math.max(...nums) : 16) + 1;
  return `PR-2026-${String(next).padStart(3, "0")}`;
}

// ---------------------------------------------------------------------------
// Creation from a Top 5 item
// ---------------------------------------------------------------------------

export type CreateProblemRecordInput = {
  id: string;
  title: string;
  owner: string;
  sponsor: string;
  severity: ProblemRecord["severity"];
  riskScore: number;
  problemStatement: string;
  start8D: boolean;
};

export function draftFromTop5(item: Top5Item, input: CreateProblemRecordInput): ProblemRecord {
  const claims = claimsForItem(item);
  const products = Array.from(new Set(claims.map((c) => c.product)));
  const carLines = Array.from(new Set([item.carLine, ...claims.map((c) => c.carLine)]));
  const start = input.start8D;

  const exposure = `${item.claims.toLocaleString()} claims · ${currency(item.cost)}${item.ratePer1000 != null ? ` · ${item.ratePer1000.toFixed(1)} C/1000` : ""}`;

  const stageList: ProblemStage[] = STAGE_TEMPLATE.map((s): ProblemStage => {
    if (s.key === "signal") {
      return {
        ...s,
        state: (start ? "Complete" : "Active") as ProblemStage["state"],
        completedOn: start ? PROTOTYPE_TODAY : undefined,
        owner: input.owner,
        summary: `Escalated from ${top5SourceLabel[item.view]} — ${item.name}. ${item.claims.toLocaleString()} claims, ${item.ratePer1000 != null ? `${item.ratePer1000.toFixed(1)} C/1000` : "rate n/a"}.`,
        evidence: {
          source: `${top5SourceLabel[item.view]} · ${item.name}`,
          evidenceConfirmed: claims.length ? "Yes" : "",
          exposure,
        },
        approvalRequired: false,
        approvalStatus: "Not Required" as const,
      };
    }
    if (s.key === "definition") {
      return {
        ...s,
        state: (start ? "Active" : "Pending") as ProblemStage["state"],
        owner: input.owner,
        summary: start ? input.problemStatement : "",
        evidence: {
          problemStatement: input.problemStatement,
          ownerAssigned: input.owner,
          scope: carLines.join(", "),
          severityConfirmed: "",
        },
        approvalRequired: false,
        approvalStatus: "Not Required" as const,
      };
    }
    const req = STAGE_REQUIREMENTS[s.key];
    return {
      ...s,
      state: "Pending" as const,
      owner: "—",
      summary: "",
      evidence: {},
      approvalRequired: req?.approvalRequired ?? false,
      approvalStatus: (req?.approvalRequired
        ? "Pending"
        : "Not Required") as ProblemStage["approvalStatus"],
    };
  });

  return {
    id: input.id,
    title: input.title,
    status: start ? "Active — Problem Definition" : "Draft — Warranty Signal",
    managementStatus: start ? "Active" : "Draft",
    linkedClaimIds: claims.map((c) => c.id),
    linkedClaims: claims,
    sourceKey: top5SourceKey(item),

    severity: input.severity,
    safetySeverity: "Not yet assessed",
    riskScore: input.riskScore,
    owner: input.owner,
    sponsor: input.sponsor || "—",
    openedOn: PROTOTYPE_TODAY,
    daysOpen: 0,
    source: `${top5SourceLabel[item.view]} · ${item.name}`,
    products: products.length ? products : [item.name],
    carLines,
    component: item.component,
    supplier: item.supplier,
    failureMode: item.failureMode,
    totalClaims: item.claims,
    warrantyCost: item.cost,
    claimsPer1000: item.ratePer1000 ?? 0,
    linkedCaseId: start ? (item.linkedCaseId ?? "8D — to be assigned") : (item.linkedCaseId ?? ""),
    top5ItemId: item.id,
    narrative: input.problemStatement,
    spcSignal: item.spcSignal ?? "No SPC special-cause signal recorded for this issue.",
    predictiveSignal: item.repeatOccurrence
      ? `Repeat occurrence — ${item.repeatCount} prior recurrence(s) of this failure signature.`
      : "First occurrence of this failure signature.",
    origin: "created",
    isDraft: !start,
    trend: item.trend,
    trendPct: item.trendPct,
    repeatOccurrence: item.repeatOccurrence,
    repeatCount: item.repeatCount,
    customerImpact: item.customerImpact,
    stages: stageList,
    eightD: start
      ? [
          {
            code: "D1",
            label: "Team",
            state: "Active",
            owner: input.owner,
            note: "Cross-functional team being chartered.",
          },
          {
            code: "D2",
            label: "Problem Description",
            state: "Active",
            owner: input.owner,
            note: input.problemStatement,
          },
          ...[
            "D3:Interim Containment",
            "D4:Root Cause",
            "D5:Permanent Corrective Action",
            "D6:Implement & Validate",
            "D7:Prevent Recurrence",
            "D8:Team Recognition / Closure",
          ].map((s) => {
            const [code, label] = s.split(":");
            return { code, label, state: "Pending" as const, owner: "—", note: "" };
          }),
        ]
      : [],
    rootCause: { ...emptyRootCause },
    correctiveActions: [],
    engineeringChanges: [],
    validation: [],
    effectiveness: [],
    activity: [
      {
        ts: `${PROTOTYPE_TODAY} 00:00`,
        actor: input.owner,
        action: `Problem Record ${input.id} created from ${top5SourceLabel[item.view]} — ${item.name}`,
        clause: "IATF 16949 §9.1.1.1",
      },
      ...(start
        ? [
            {
              ts: `${PROTOTYPE_TODAY} 00:01`,
              actor: input.owner,
              action: "8D initiated — D1 team chartering and D2 problem description opened",
              clause: "IATF 16949 §10.2.3",
            },
          ]
        : []),
    ],
  };
}
